// FloatingStressMenu - Context menu for stress override actions
// Epic 07: Interactive stress override UI with @floating-ui/dom positioning
// Floating menu with ARIA support and keyboard navigation

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { computePosition, flip, shift, offset } from '@floating-ui/dom'
import type { LexicalEditor } from 'lexical'

// ============================================================================
// Types
// ============================================================================

export interface SyllableTarget {
  nodeKey: string
  word: string
  syllableIndex: number
  currentLevel: number
}

export interface FloatingStressMenuProps {
  /** Editor instance for dispatching commands */
  editor: LexicalEditor
  /** Target syllable information */
  target: SyllableTarget
  /** Reference element for positioning (the clicked stress mark) */
  referenceElement: HTMLElement
  /** Editor container for portal target */
  editorContainer: HTMLElement
  /** Callback when menu should close */
  onClose: () => void
  /** Callback to dispatch stress commands */
  onCycleLevel: (target: SyllableTarget) => void
  onSetLevel: (target: SyllableTarget, level: 0 | 1 | 2) => void
  onApplyPattern: (target: SyllableTarget, pattern: 'trochee' | 'iamb' | 'dactyl' | 'anapest') => void
  onClearOverrides: (target: SyllableTarget) => void
}

// ============================================================================
// Component
// ============================================================================

/**
 * FloatingStressMenu - Context menu for stress override actions
 *
 * Renders floating menu near clicked syllable with @floating-ui/dom.
 * Portal to editor container (NOT document.body), keyboard navigation, ARIA roles.
 * Focus trap with Tab/Shift+Tab, closes on outside click/Escape.
 *
 * Epic 07 Requirements:
 * - Position with offset(6), flip(), shift({ padding: 8 })
 * - Show current stress level with filled SVG glyph (L0 ˘, L1 ´, L2 `)
 * - Actions: cycle, set L0/L1/L2, apply patterns, clear overrides
 * - ARIA: role="menu" container, role="menuitem" items
 * - data-testid on interactive elements
 * - Close on outside click, Escape, action completion, editor blur
 *
 * Reference: .local_docs/lexical/articles/overlay-positioning.md - Floating UI patterns
 */
export function FloatingStressMenu({
  editor,
  target,
  referenceElement,
  editorContainer,
  onClose,
  onCycleLevel,
  onSetLevel,
  onApplyPattern,
  onClearOverrides,
}: FloatingStressMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const focusedIndexRef = useRef(0)

  // Opening move: Calculate position using @floating-ui/dom
  useEffect(() => {
    if (!menuRef.current || !referenceElement) return

    const updatePosition = async () => {
      const { x, y } = await computePosition(referenceElement, menuRef.current!, {
        placement: 'bottom-start',
        middleware: [
          offset(6), // 6px spacing from reference element
          flip(), // Flip to opposite side if no space
          shift({ padding: 8 }), // Shift to stay within viewport with 8px padding
        ],
      })

      // computePosition returns viewport-relative coordinates
      setPosition({ x, y })
    }

    updatePosition()

    // Victory lap: Update position on scroll/resize
    const handleUpdate = () => updatePosition()
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
    }
  }, [referenceElement])

  // Main play: Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    // Small delay to avoid closing immediately from the opening click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  // Time to tackle the tricky bit: Keyboard navigation and Escape handling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]')
      if (!menuItems) return

      switch (event.key) {
        case 'Escape':
          event.preventDefault()
          onClose()
          break

        case 'Tab': {
          event.preventDefault()
          const direction = event.shiftKey ? -1 : 1
          focusedIndexRef.current = (focusedIndexRef.current + direction + menuItems.length) % menuItems.length
          ;(menuItems[focusedIndexRef.current] as HTMLElement).focus()
          break
        }

        case 'ArrowDown':
          event.preventDefault()
          focusedIndexRef.current = (focusedIndexRef.current + 1) % menuItems.length
          ;(menuItems[focusedIndexRef.current] as HTMLElement).focus()
          break

        case 'ArrowUp':
          event.preventDefault()
          focusedIndexRef.current = (focusedIndexRef.current - 1 + menuItems.length) % menuItems.length
          ;(menuItems[focusedIndexRef.current] as HTMLElement).focus()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Big play: Close on editor blur
  useEffect(() => {
    const rootElement = editor.getRootElement()
    if (!rootElement) return

    const handleBlur = () => {
      // Small delay to allow menu interactions before closing
      setTimeout(() => {
        if (!menuRef.current?.contains(document.activeElement)) {
          onClose()
        }
      }, 100)
    }

    rootElement.addEventListener('blur', handleBlur, true)
    return () => rootElement.removeEventListener('blur', handleBlur, true)
  }, [editor, onClose])

  // Victory lap: Focus first item on mount
  useEffect(() => {
    const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]')
    if (menuItems && menuItems.length > 0) {
      ;(menuItems[0] as HTMLElement).focus()
    }
  }, [])

  // Main play: Action handlers that close menu after execution
  const handleAction = (action: () => void) => {
    action()
    onClose()
  }

  // Map stress level to CSS color class
  const getLevelColorClass = (level: number): string => {
    switch (level) {
      case 1:
        return 'stress-context-menu__glyph--l1'
      case 2:
        return 'stress-context-menu__glyph--l2'
      default:
        return 'stress-context-menu__glyph--l0'
    }
  }

  // Render inline SVG stress mark matching overlay symbols
  // Uses same filled paths as StressMarkSymbols.tsx for visual consistency
  const renderStressSvg = (level: number, size: number = 12) => {
    let path: string

    if (level === 1) {
      // Primary stress - acute accent (´) - filled parallelogram slanting upper-right
      path = 'M 2.65,3.82 3.38,0 H 5.8 L 4.5,3.82 Z'
    } else if (level === 2) {
      // Secondary stress - grave accent (`) - filled parallelogram slanting upper-left
      path = 'M 5.48,3.85 4.54,0.03 H 2.12 l 1.3,3.82 z'
    } else {
      // Unstressed - breve (˘) - filled U-shaped curve
      path = 'M 4,3.62 Q 2.2,3.62 1.09,2.63 0,1.62 0,0 h 1.78 q 0,0.97 0.6,1.57 0.6,0.6 1.59,0.6 Q 5,2.17 5.59,1.57 6.22,0.97 6.22,0 H 8 Q 8,1.62 6.91,2.63 5.8,3.62 4,3.62 Z'
    }

    return (
      <svg
        width={size}
        height={size * 1.25}
        viewBox="0 0 8 10"
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
        aria-hidden="true"
      >
        <path d={path} fill="currentColor" />
      </svg>
    )
  }

  // Victory lap: Render menu with portal to editor container
  return createPortal(
    <div
      ref={menuRef}
      className="stress-context-menu"
      role="menu"
      aria-label="Stress override menu"
      data-testid="stress-context-menu"
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999, // Ensure menu is above other elements
      }}
    >
      {/* Current stress level indicator */}
      <div className="stress-context-menu__header" data-testid="menu-current-level">
        <span className="stress-context-menu__label">Current:</span>
        <span className={`stress-context-menu__glyph ${getLevelColorClass(target.currentLevel)}`}>
          {renderStressSvg(target.currentLevel)}
        </span>
        <span className="stress-context-menu__level-text">L{target.currentLevel}</span>
      </div>

      <div className="stress-context-menu__divider" />

      {/* Cycle action */}
      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item"
        data-testid="menu-action-cycle"
        onClick={() => handleAction(() => onCycleLevel(target))}
      >
        <span className="stress-context-menu__icon">🔄</span>
        <span>Cycle Stress (L0→L1→L2→L0)</span>
      </button>

      <div className="stress-context-menu__divider" />

      {/* Set level actions */}
      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item"
        data-testid="menu-action-set-l1"
        onClick={() => handleAction(() => onSetLevel(target, 1))}
      >
        <span className="stress-context-menu__glyph stress-context-menu__glyph--l1">
          {renderStressSvg(1)}
        </span>
        <span>Set Primary (L1)</span>
      </button>

      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item"
        data-testid="menu-action-set-l2"
        onClick={() => handleAction(() => onSetLevel(target, 2))}
      >
        <span className="stress-context-menu__glyph stress-context-menu__glyph--l2">
          {renderStressSvg(2)}
        </span>
        <span>Set Secondary (L2)</span>
      </button>

      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item"
        data-testid="menu-action-set-l0"
        onClick={() => handleAction(() => onSetLevel(target, 0))}
      >
        <span className="stress-context-menu__glyph stress-context-menu__glyph--l0">
          {renderStressSvg(0)}
        </span>
        <span>Set Unstressed (L0)</span>
      </button>

      <div className="stress-context-menu__divider" />

      {/* Pattern actions */}
      <div className="stress-context-menu__section-label">Apply Pattern to Word</div>

      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item"
        data-testid="menu-action-pattern-trochee"
        onClick={() => handleAction(() => onApplyPattern(target, 'trochee'))}
      >
        <span className="stress-context-menu__pattern">
          Trochee (
          <span className="stress-context-menu__glyph--l1">{renderStressSvg(1, 10)}</span>
          <span className="stress-context-menu__glyph--l0">{renderStressSvg(0, 10)}</span>
          )
        </span>
      </button>

      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item"
        data-testid="menu-action-pattern-iamb"
        onClick={() => handleAction(() => onApplyPattern(target, 'iamb'))}
      >
        <span className="stress-context-menu__pattern">
          Iamb (
          <span className="stress-context-menu__glyph--l0">{renderStressSvg(0, 10)}</span>
          <span className="stress-context-menu__glyph--l1">{renderStressSvg(1, 10)}</span>
          )
        </span>
      </button>

      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item"
        data-testid="menu-action-pattern-dactyl"
        onClick={() => handleAction(() => onApplyPattern(target, 'dactyl'))}
      >
        <span className="stress-context-menu__pattern">
          Dactyl (
          <span className="stress-context-menu__glyph--l1">{renderStressSvg(1, 10)}</span>
          <span className="stress-context-menu__glyph--l0">{renderStressSvg(0, 10)}</span>
          <span className="stress-context-menu__glyph--l0">{renderStressSvg(0, 10)}</span>
          )
        </span>
      </button>

      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item"
        data-testid="menu-action-pattern-anapest"
        onClick={() => handleAction(() => onApplyPattern(target, 'anapest'))}
      >
        <span className="stress-context-menu__pattern">
          Anapest (
          <span className="stress-context-menu__glyph--l0">{renderStressSvg(0, 10)}</span>
          <span className="stress-context-menu__glyph--l0">{renderStressSvg(0, 10)}</span>
          <span className="stress-context-menu__glyph--l1">{renderStressSvg(1, 10)}</span>
          )
        </span>
      </button>

      <div className="stress-context-menu__divider" />

      {/* Clear overrides */}
      <button
        type="button"
        role="menuitem"
        className="stress-context-menu__item stress-context-menu__item--danger"
        data-testid="menu-action-clear"
        onClick={() => handleAction(() => onClearOverrides(target))}
      >
        <span className="stress-context-menu__icon">✕</span>
        <span>Clear All Overrides</span>
      </button>
    </div>,
    editorContainer
  )
}

// Victory lap: FloatingStressMenu complete with floating-ui positioning and ARIA support
// Portal to editor container (not document.body), focus trap, keyboard navigation
// Ready for StressInteractionPlugin integration
